import React, { useState, useEffect, useContext } from "react";

import { useHistory, useLocation, useParams } from "react-router-dom";

import ProductHeaderCard from "../../components/LabStore/ProductHeaderCard";
import DeveloperCard from "../../components/LabStore/DeveloperCard";
import ProductSpecsCard from "../../components/LabStore/ProductSpecsCard";
import ProductTags from "../../components/Tags/ProductTags";
import ProductSlider from "../../components/Sliders/ProductSlider";
import ProductDetailsCard from "../../components/LabStore/ProductDetailsCard";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import ProductsAPI from "../../API/products.api";
import OrganizationSubscriptionsAPI from "../../API/organizationSubscriptions.api";

import TableLoader from "../../components/Loaders/TableLoader";
import LogCapture from "../../utils/LogManagement/LogHelper";
import HelpAndSupportCard from "../../components/Cards/HelpAndSupportCard";
import { isEmpty } from "lodash";
import { Trans } from "react-i18next";
import UserContext from "../../lib/UserContext";
import { t } from "i18next";
import { PRODUCT_TYPES, TRIAL_DAYS, UUIDS } from "../../utils/constants";

const LabStoreProduct = ({
    product,
    previewParameterName,
    isPreview,
    isReview = false,
}) => {
    const [model, setModel] = useState(null);
    const [isModelLoading, setIsModelLoading] = useState(false);
    const [productId, setProductId] = useState(null);
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [isSubscribed] = useState(false);
    const [parametersNames, setParametersNames] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [includePromoCode, setIncludePromoCode] = useState(false);
    const [promoCode, setPromoCode] = useState("");
    const [applyCodeFlag, setApplyCodeFlag] = useState(false);
    const currentUser = useContext(UserContext).getUser();

    const params = useParams();
    const history = useHistory();
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const queryPromoCode = query.get("promotion-code");

    useEffect(() => {
        if (queryPromoCode) {
            setPromoCode(queryPromoCode);
            setIncludePromoCode(true);
            setApplyCodeFlag(true);
        }
    }, []);

    useEffect(() => {
        if (params.id) {
            fetchProduct(params.id);
            setProductId(params.id);
        }
    }, [params]);

    useEffect(() => {
        setModel(product);
    }, [product]);

    useEffect(() => {
        setParametersNames(previewParameterName);
    }, [previewParameterName]);

    const goToPreviousPath = () => {
        history.goBack();
    };

    const fetchProduct = async (id) => {
        try {
            setIsModelLoading(true);
            const result = await ProductsAPI.getProductDetails(id);
            setModel(customMapping(result));
            if (result) {
                const paraNames =
                    result.parameters &&
                    result.parameters.map((parameter) => {
                        return parameter.parameterName;
                    });
                setParametersNames(paraNames);
            } else {
                history.push("/page-not-found");
            }
        } catch (error) {
            ErrorAlert(error);
        } finally {
            setIsModelLoading(false);
        }
    };

    const customMapping = (modelData) => {
        const newModelData = { ...modelData };

        if (newModelData?.developedBy?.id === UUIDS.aunirId) {
            newModelData.parameters = newModelData?.parameters?.map(
                (parameter) => ({ ...parameter, maxValue: 0, minValue: 0 })
            );
        }

        if (newModelData.hasTrial)
            newModelData.trialsDays = TRIAL_DAYS[newModelData.trialsDays];

        return newModelData;
    };

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(window.location.href);
        SuccessAlert(t("copiedMessage", { entity: t("url") }));
    };

    const handleSubscription = async () => {
        if (model) {
            const subscriptionPlanId =
                model?.subscriptionPlans[selectedPlan]?.id;
            setIsSubscribing(true);
            // return OrganizationSubscriptionsAPI.subscribe({
            //     subscriptionPlanId,
            // })
            return OrganizationSubscriptionsAPI.createCheckoutSession(
                subscriptionPlanId,
                includePromoCode ? promoCode : ""
            )
                .then((res) => {
                    setIsSubscribing(false);
                    LogCapture.info(
                        `Subscription plan subscribed with id: ${subscriptionPlanId}`
                    );
                    return res;
                })
                .catch((error) => {
                    ErrorAlert("Couldn't subscribe to this product!");
                    LogCapture.error(
                        `Couldn't subscribe to subscription plan with id: ${subscriptionPlanId}, error: ${error}`
                    );
                });
        }
    };

    const contactAndSupportFields = [
        {
            path: "/support",
            icon: "fas fa-comments",
            label: t("contactSupport"),
        },
        {
            icon: "fas fa-clipboard-list",
            url: model?.developedBy?.termsAndConditions,
            label: t("termsOfUse"),
            disabled: !model?.developedBy?.termsAndConditions,
        },
        {
            icon: "fas fa-key",
            url: model?.developedBy?.privacyPolicy,
            label: t("privacyPolicy"),
            disabled: !model?.developedBy?.privacyPolicy,
        },
        {
            icon: "fas fa-copy",
            url: model?.productGuideFilePath,
            label: t("productGuide"),
            disabled: !model?.productGuideFilePath,
            fileName: "product-guide.pdf",
        },
        {
            icon: "fas fa-share-square",
            label: t("shareLink"),
            action: handleCopyToClipboard,
        },
    ];

    const getEnforceOutlierMessage = () => {
        const enforceOutlierMessages = {
            true: [t("enforceOutlierMessage")],
            false: [t("notEnforcingOutlierMessage")],
        };

        if (model)
            return (
                enforceOutlierMessages[model?.enforceOutlierDetection] ||
                enforceOutlierMessages[model?.enforceOutlierDetection?.value] ||
                enforceOutlierMessages.false
            );
    };

    if (isModelLoading || (isEmpty(product) && isPreview))
        return <TableLoader isLoading={true} />;
    return (
        <>
            <div className="product-section">
                {!isPreview && (
                    <button
                        className="default-transparent-btn mb-5"
                        onClick={goToPreviousPath}
                    >
                        <i className="fas fa-caret-left"></i>
                        <span className="ml-2">{t("goBack")}</span>
                    </button>
                )}
                <div className="grid-2-cols-min-max">
                    <div className="product-description">
                        <div className="product-header card-table shadow mb-5">
                            <ProductHeaderCard
                                model={model}
                                productId={productId}
                                isPreview={isPreview}
                            />
                        </div>
                        <div className="mb-7">
                            <div className="product-slider">
                                <ProductSlider
                                    className="product-details"
                                    images={model?.images}
                                />
                            </div>
                        </div>
                        <ProductDetailsCard
                            model={model}
                            isPreview={isPreview}
                            isReview={isReview}
                        />
                    </div>
                    <div className="product-details">
                        <div className="product-header card-table shadow mb-4 flex">
                            <div className="market-prods flex items-center justify-between">
                                <DeveloperCard
                                    model={model}
                                    isPreview={isPreview}
                                />
                            </div>
                        </div>
                        <ProductSpecsCard
                            model={model}
                            parameters={parametersNames}
                            handleSubscription={handleSubscription}
                            isSubscribing={isSubscribing}
                            isSubscribed={
                                isSubscribed || model?.isSubscribed || false
                            }
                            productId={productId}
                            selectedPlan={selectedPlan}
                            setSelectedPlan={setSelectedPlan}
                            currentUser={currentUser}
                            isPreview={isPreview}
                            promoCode={promoCode}
                            setPromoCode={setPromoCode}
                            includePromoCode={includePromoCode}
                            setIncludePromoCode={setIncludePromoCode}
                            applyCodeVerification={applyCodeFlag}
                        />
                        <div className="product-header">
                            <div className="product-privacy-info">
                                <div className="mb-4">
                                    <h4>
                                        <Trans i18nKey="supportedAccessories">
                                            Supported Accessories
                                        </Trans>
                                    </h4>
                                    <ProductTags
                                        tags={model?.supportedAccessories}
                                    />
                                </div>
                                {model?.type !== PRODUCT_TYPES.bundle && (
                                    <div className="mb-4">
                                        <h4>
                                            <Trans i18nKey="outlierDetection">
                                                Outlier Detection
                                            </Trans>
                                        </h4>
                                        <ProductTags
                                            tags={getEnforceOutlierMessage()}
                                        />
                                    </div>
                                )}
                                <div className="mb-4">
                                    <h4>
                                        <Trans i18nKey="supportedCountries">
                                            Supported Countries
                                        </Trans>
                                    </h4>
                                    <ProductTags
                                        tags={
                                            isEmpty(model?.supportedCountries)
                                                ? [
                                                      <>
                                                          <div className="m-0">
                                                              <i className="fas fa-globe-americas mr-1"></i>
                                                              <Trans i18nKey="global">
                                                                  Global
                                                              </Trans>
                                                          </div>
                                                      </>,
                                                  ]
                                                : model?.supportedCountries?.map(
                                                      (country) => {
                                                          return (
                                                              <>
                                                                  <div
                                                                      className={`flag-icon flag-icon-${country?.alpha2Code?.toLowerCase()}`}
                                                                  ></div>
                                                                  {` ${country?.label}`}
                                                              </>
                                                          );
                                                      }
                                                  )
                                        }
                                    />
                                </div>
                                <div className="mb-4">
                                    <h4>
                                        <Trans i18nKey="industries">
                                            Industries
                                        </Trans>
                                    </h4>
                                    <ProductTags tags={model?.industries} />
                                </div>
                                <div className="">
                                    <h4>
                                        <Trans i18nKey="tags">Tags</Trans>
                                    </h4>
                                    <ProductTags tags={model?.tags} />
                                </div>
                            </div>
                        </div>
                        {!isPreview && (
                            <div className="product-header">
                                <HelpAndSupportCard
                                    fields={contactAndSupportFields}
                                    containerClassName="mb-4"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default LabStoreProduct;
